// PO Spec 2026-06-21 (post-M3 review) — first-class dashboard tile for
// the anonymised wellbeing aggregate (§6.4). Surfaces both the Amber
// aggregate and the Red-flag aggregate alongside it, with a drilldown
// to the per-category list. The tile is a presentation layer over the
// structured server response — it MUST NOT compute its own thresholds
// or invent fields the API didn't send.
//
// Privacy posture (DESIGN §6.4 + post-M3 review):
//   - Pool-at-floor: per-category count lines visible, threshold echoed.
//   - Pool-below-floor: single "below the privacy floor of N" note; the
//     category names are intentionally NOT listed (naming them would be
//     a positive signal that ≥1 event occurred in that category on a
//     small team).
//   - Zero state: "no amber/red signals in the last 90 days".
//   - Both HR_Admin and HR_Viewer see the same anonymised shape; only
//     per-case deep-links differ on the flag feed (H7/H8 from the
//     existing dashboard).

import { useState } from 'react';
import {
  type AmberAggregate,
  type WellbeingCategoryCount,
  humaniseClassifierCategory,
} from '@besideu/shared/hr-dashboard';

interface AmberAggregateTileProps {
  data: AmberAggregate;
}

// The two aggregate blocks (Amber + Red) share the wire shape. Local
// alias keeps the component body self-documenting.
type AggregateBlock = AmberAggregate['amber'];

export function AmberAggregateTile({ data }: AmberAggregateTileProps) {
  return (
    <section className="rounded-lg border border-border bg-background p-6">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Anonymised wellbeing signals</h2>
        <p className="text-xs text-muted-foreground">Last 90 days · counts only · no PII</p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <BlockSummary label="Amber" tone="warn" block={data.amber} />
        <BlockSummary label="Red" tone="alert" block={data.red} />
      </div>
    </section>
  );
}

function BlockSummary({
  label,
  tone,
  block,
}: {
  label: string;
  tone: 'warn' | 'alert';
  block: AggregateBlock;
}) {
  // Mirrors `BucketTile` tone palette but for the anonymised
  // aggregate. The total is always shown (it's a count, not
  // attribution) — even when `suppressed: true`, the operator needs
  // to see "events exist but pool too small" so they understand
  // the suppression isn't a data-pipeline gap.
  // Tone carried by border + fill; text stays `text-foreground` for WCAG AA
  // (traffic-amber is too light as small foreground text). `alert` keeps the
  // red emphasis — large-text count clears the 3:1 threshold.
  const toneClass =
    tone === 'alert'
      ? 'border-traffic-red/40 bg-traffic-red/5 text-traffic-red'
      : 'border-traffic-amber/40 bg-traffic-amber/10 text-foreground';
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <p className="text-3xl font-semibold tabular-nums" aria-label={`${label} signal total`}>
        {block.total}
      </p>
      <p className="mt-1 text-sm font-medium">{label} signals</p>
      <BlockBody label={label} block={block} />
    </div>
  );
}

// The body copy differs by suppression state. We deliberately do NOT
// invent a "below floor" headline count when `suppressed: true` —
// the total is the count, the note explains why the per-category
// list is empty. This matches the server's §6.4 posture.
function BlockBody({ label, block }: { label: string; block: AggregateBlock }) {
  if (block.suppressed) {
    if (block.total === 0) {
      return (
        <p className="mt-2 text-xs">
          No {label.toLowerCase()}-level signals recorded in the last 90 days.
        </p>
      );
    }
    return (
      <p className="mt-2 text-xs">
        Pool below the privacy floor of {block.threshold}. Per-category counts are not surfaced for
        privacy.
      </p>
    );
  }
  return <CategoryList categories={block.categories} />;
}

function CategoryList({ categories }: { categories: ReadonlyArray<WellbeingCategoryCount> }) {
  const [expanded, setExpanded] = useState(false);
  // Default-collapsed: the headline count + tone is the primary
  // signal; the per-category list is the drilldown. The toggle is
  // the same pattern as the existing `FlagFeed` Acknowledge button
  // — small, labelled, and reachable from a single tap.
  if (categories.length === 0) return null;
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-xs font-medium underline-offset-2 hover:underline"
        aria-expanded={expanded}
        aria-controls={`agg-categories-${categories.map((c) => c.category).join('-')}`}
      >
        {expanded ? 'Hide' : 'Show'} per-category breakdown
      </button>
      {expanded ? (
        <ul
          id={`agg-categories-${categories.map((c) => c.category).join('-')}`}
          className="mt-2 space-y-1"
        >
          {categories.map((c) => (
            <li key={c.category} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-mono">{humaniseClassifierCategory(c.category)}</span>
              <span className="tabular-nums">{c.count}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
